import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchUserAttributes, updateUserAttributes } from 'aws-amplify/auth';
import { apiService } from '../services/api';

interface ProfileCompletionFormData {
  title: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  phoneNumber: string;
  username: string;
}

const ProfileCompletionForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ProfileCompletionFormData>({
    title: '',
    firstName: '',
    lastName: '',
    countryCode: '',
    phoneNumber: '',
    username: '',
  });
  const [errors, setErrors] = useState<Partial<ProfileCompletionFormData>>({});
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [step, setStep] = useState<'profile' | 'verify-phone'>('profile');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  // Title options
  const titles = ['Mr', 'Mrs', 'Ms', 'Dr'];

  // Country codes with common countries
  const countryCodes = [
    { code: '+1', country: 'United States/Canada' },
    { code: '+44', country: 'United Kingdom' },
    { code: '+33', country: 'France' },
    { code: '+49', country: 'Germany' },
    { code: '+39', country: 'Italy' },
    { code: '+34', country: 'Spain' },
    { code: '+31', country: 'Netherlands' },
    { code: '+32', country: 'Belgium' },
    { code: '+41', country: 'Switzerland' },
    { code: '+43', country: 'Austria' },
    { code: '+45', country: 'Denmark' },
    { code: '+46', country: 'Sweden' },
    { code: '+47', country: 'Norway' },
    { code: '+358', country: 'Finland' },
    { code: '+353', country: 'Ireland' },
    { code: '+351', country: 'Portugal' },
    { code: '+30', country: 'Greece' },
    { code: '+48', country: 'Poland' },
    { code: '+420', country: 'Czech Republic' },
    { code: '+36', country: 'Hungary' },
    { code: '+91', country: 'India' },
    { code: '+86', country: 'China' },
    { code: '+81', country: 'Japan' },
    { code: '+82', country: 'South Korea' },
    { code: '+61', country: 'Australia' },
    { code: '+64', country: 'New Zealand' },
    { code: '+27', country: 'South Africa' },
    { code: '+55', country: 'Brazil' },
    { code: '+52', country: 'Mexico' },
    { code: '+54', country: 'Argentina' },
  ];

  // Check authentication on mount and retrieve userType
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await fetchUserAttributes();
        // Retrieve userType from localStorage (Requirement 6.2)
        const userType = localStorage.getItem('userType');
        if (userType) {
          console.log('User type:', userType);
        }
      } catch (error) {
        console.error('User not authenticated:', error);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Apply character restrictions
    let sanitizedValue = value;
    
    if (name === 'phoneNumber') {
      // Only allow digits
      sanitizedValue = value.replace(/\D/g, '');
      // Enforce max length of 12 digits
      if (sanitizedValue.length > 12) {
        sanitizedValue = sanitizedValue.slice(0, 12);
      }
    } else if (name === 'username') {
      // Only allow alphanumeric and -_.@
      sanitizedValue = value.replace(/[^a-zA-Z0-9\-_.@]/g, '');
      // Enforce max length of 12 characters
      if (sanitizedValue.length > 12) {
        sanitizedValue = sanitizedValue.slice(0, 12);
      }
    } else if (name === 'firstName' || name === 'lastName') {
      // Enforce max length of 12 characters
      if (value.length > 12) {
        sanitizedValue = value.slice(0, 12);
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ProfileCompletionFormData> = {};

    if (!formData.title) {
      newErrors.title = 'Title is required';
    }

    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.length > 12) {
      newErrors.firstName = 'First name must be 12 characters or less';
    }

    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.length > 12) {
      newErrors.lastName = 'Last name must be 12 characters or less';
    }

    if (!formData.countryCode) {
      newErrors.countryCode = 'Country code is required';
    }

    if (!formData.phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\d+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Phone number must contain only digits';
    } else if (formData.phoneNumber.length > 12) {
      newErrors.phoneNumber = 'Phone number must be 12 digits or less';
    }

    if (!formData.username) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-zA-Z0-9\-_.@]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and -_.@';
    } else if (formData.username.length > 12) {
      newErrors.username = 'Username must be 12 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      await updateUserAttributes({
        userAttributes: {
          'custom:title': formData.title,
          name: formData.firstName,
          family_name: formData.lastName,
          given_name: formData.firstName,
          phone_number: `${formData.countryCode}${formData.phoneNumber}`,
          'custom:username': formData.username,
        },
      });

      await apiService.sendPhoneOtp(`${formData.countryCode}${formData.phoneNumber}`);
      setStep('verify-phone');
    } catch (error: any) {
      console.error('Profile update error:', error);
      setGeneralError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');

    if (!otpCode || otpCode.length < 6) {
      setOtpError('Please enter the 6-digit code sent to your phone.');
      return;
    }

    setLoading(true);
    try {
      await apiService.verifyPhoneOtp(otpCode, `${formData.countryCode}${formData.phoneNumber}`);
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Phone verification error:', error);
      setOtpError(error.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setOtpError('');
    setResendLoading(true);
    try {
      await apiService.sendPhoneOtp(`${formData.countryCode}${formData.phoneNumber}`);
    } catch (error: any) {
      setOtpError(error.message || 'Failed to resend code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div className="form-container">
        {step === 'verify-phone' ? (
          <>
            <h2 className="form-title">Verify Your Phone</h2>
            <p style={{ textAlign: 'center', color: '#666', marginBottom: '2rem' }}>
              We sent a 6-digit code to {formData.countryCode} {formData.phoneNumber}. Enter it below to confirm your number.
            </p>
            <form onSubmit={handleVerifyPhone}>
              <div className="form-group">
                <label className="form-label" htmlFor="otpCode">Verification Code</label>
                <input
                  type="text"
                  id="otpCode"
                  className={`form-input ${otpError ? 'error' : ''}`}
                  value={otpCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtpCode(val);
                    setOtpError('');
                  }}
                  maxLength={6}
                  placeholder="123456"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                />
                {otpError && <span className="form-error">{otpError}</span>}
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ width: '100%', marginTop: '1rem' }}
              >
                {loading ? <span className="loading-spinner"></span> : 'Verify Phone'}
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleResendCode}
                disabled={resendLoading}
                style={{ width: '100%', marginTop: '0.75rem' }}
              >
                {resendLoading ? 'Sending...' : 'Resend Code'}
              </button>
            </form>
          </>
        ) : (
          <>
        <h2 className="form-title">Complete Your Profile</h2>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '2rem' }}>
          Please provide the following information to complete your registration
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="title">Title</label>
            <select
              id="title"
              name="title"
              className={`form-input ${errors.title ? 'error' : ''}`}
              value={formData.title}
              onChange={handleChange}
            >
              <option value="">Select title</option>
              {titles.map((title) => (
                <option key={title} value={title}>
                  {title}
                </option>
              ))}
            </select>
            {errors.title && <span className="form-error">{errors.title}</span>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              className={`form-input ${errors.firstName ? 'error' : ''}`}
              value={formData.firstName}
              onChange={handleChange}
              maxLength={12}
              placeholder="John"
            />
            {errors.firstName && <span className="form-error">{errors.firstName}</span>}
            <small style={{ fontSize: '0.875rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
              Maximum 12 characters
            </small>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              className={`form-input ${errors.lastName ? 'error' : ''}`}
              value={formData.lastName}
              onChange={handleChange}
              maxLength={12}
              placeholder="Doe"
            />
            {errors.lastName && <span className="form-error">{errors.lastName}</span>}
            <small style={{ fontSize: '0.875rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
              Maximum 12 characters
            </small>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="countryCode">Country Code</label>
            <select
              id="countryCode"
              name="countryCode"
              className={`form-input ${errors.countryCode ? 'error' : ''}`}
              value={formData.countryCode}
              onChange={handleChange}
            >
              <option value="">Select country code</option>
              {countryCodes.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.code} ({item.country})
                </option>
              ))}
            </select>
            {errors.countryCode && <span className="form-error">{errors.countryCode}</span>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="phoneNumber">Phone Number</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              className={`form-input ${errors.phoneNumber ? 'error' : ''}`}
              value={formData.phoneNumber}
              onChange={handleChange}
              maxLength={12}
              placeholder="1234567890"
            />
            {errors.phoneNumber && <span className="form-error">{errors.phoneNumber}</span>}
            <small style={{ fontSize: '0.875rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
              Maximum 12 digits, numbers only
            </small>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              className={`form-input ${errors.username ? 'error' : ''}`}
              value={formData.username}
              onChange={handleChange}
              maxLength={12}
              placeholder="john.doe"
            />
            {errors.username && <span className="form-error">{errors.username}</span>}
            <small style={{ fontSize: '0.875rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
              Maximum 12 characters, alphanumeric and -_.@ only
            </small>
          </div>

          {generalError && <div className="form-error" style={{ marginBottom: '1rem' }}>{generalError}</div>}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ width: '100%', marginTop: '1rem' }}
          >
            {loading ? <span className="loading-spinner"></span> : 'Complete Profile'}
          </button>
        </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileCompletionForm;
